/* eslint-disable rxjs/no-subject-value */
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { LocalDate } from '@fcom/utils/tz-date';

import { DateSelection, Day } from '../../../utils/date.interface';

@Injectable()
export class CalendarService {
  // Selection state
  private _datesSubject$: BehaviorSubject<DateSelection> = new BehaviorSubject<DateSelection>({
    startDate: undefined,
    endDate: undefined,
  });
  public readonly datesSelected$: Observable<DateSelection> = this._datesSubject$
    .asObservable()
    .pipe(distinctUntilChanged());

  // Minimum and maximum selectable dates
  private _selectableDatesRange$: BehaviorSubject<DateSelection> = new BehaviorSubject<DateSelection>({
    startDate: undefined,
    endDate: undefined,
  });

  // Minimum and maximum selectable dates
  private _isDateRange$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public readonly isDateRange$: Observable<boolean> = this._isDateRange$.asObservable();

  // Focusable active cell (last focused) state
  private _activeCell$: BehaviorSubject<LocalDate> = new BehaviorSubject<LocalDate>(null);
  public readonly activeCell$: Observable<LocalDate> = this._activeCell$.asObservable();

  // Hover state
  private _hoveredDay$: BehaviorSubject<Day> = new BehaviorSubject<Day>(null);
  public readonly hoveredDay$: Observable<Day> = this._hoveredDay$.asObservable();

  // Visible month state
  private _visibleMonthIndex$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  public readonly visibleMonthIndex$: Observable<number> = this._visibleMonthIndex$.asObservable();

  selectDay(day: Day): void {
    const currentSelection: DateSelection = this._datesSubject$.value;
    if (
      !currentSelection.startDate ||
      currentSelection.endDate ||
      day.date.lt(currentSelection.startDate) ||
      !this._isDateRange$.value
    ) {
      this._datesSubject$.next({
        startDate: day.date,
        endDate: undefined,
      });
    } else {
      this._datesSubject$.next(Object.assign({}, this._datesSubject$.value, { endDate: day.date }));
    }

    this.setActiveCell(day.date);
  }

  setDate(date: LocalDate | undefined, isEndDate: boolean): void {
    const currentDatesSelection: DateSelection = this._datesSubject$.value;
    const currentCorrespondingDateSelection = isEndDate
      ? currentDatesSelection.endDate
      : currentDatesSelection.startDate;
    const isAlreadySelected = currentCorrespondingDateSelection && date?.equals(currentCorrespondingDateSelection);
    const isEndBeforeStart = isEndDate && currentDatesSelection.startDate && date?.lt(currentDatesSelection.startDate);
    const isOutOfRange =
      date?.lt(this._selectableDatesRange$.value.startDate) || date?.gt(this._selectableDatesRange$.value.endDate);

    if (isAlreadySelected) {
      return;
    } else if (isEndBeforeStart) {
      throw new Error('endBeforeStart');
    } else if (isOutOfRange) {
      throw new Error('outOfRange');
    } else {
      const newDateSubjecValue = isEndDate ? { endDate: date } : { startDate: date };
      this._datesSubject$.next(Object.assign({}, currentDatesSelection, newDateSubjecValue));
    }

    if (date) {
      this.setActiveCell(date);
    }
  }

  setDates(dateSelection: DateSelection): void {
    this._datesSubject$.next(dateSelection);
  }

  clearDates(): void {
    this._datesSubject$.next({
      startDate: undefined,
      endDate: undefined,
    });
  }

  setActiveCell(date: LocalDate): void {
    this._activeCell$.next(date);
  }

  setHoveredDay(date: Day): void {
    this._hoveredDay$.next(date);
  }

  setSelectableDatesRange(startDate: LocalDate, endDate: LocalDate): void {
    this._selectableDatesRange$.next({ startDate, endDate });
  }

  setVisibleMonthIndex(index: number): void {
    this._visibleMonthIndex$.next(index);
  }

  setIsDateRange(isRange: boolean): void {
    this._isDateRange$.next(isRange);
  }

  getActiveDate(): LocalDate {
    return this._activeCell$.value;
  }
}
